/**
 * CriticalObligationCard - Card for displaying critical/overdue obligations
 * Used in Gaps & Risks page Critical Issues tab
 */
import React from 'react';
import { AlertTriangle, ArrowRight, TrendingUp } from 'lucide-react';

export default function CriticalObligationCard({ obligation, onSelect }) {
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric' 
    });
  };

  const getDaysOverdue = () => {
    if (!obligation.due_date) return 0;
    const today = new Date();
    const due = new Date(obligation.due_date);
    return Math.abs(Math.ceil((today - due) / (1000 * 60 * 60 * 24)));
  };

  return (
    <div 
      className="bg-[#111111] border border-red-500/30 rounded-xl p-5 hover:bg-[#151515] transition-all cursor-pointer"
      onClick={onSelect}
      data-testid={`critical-obligation-${obligation.id}`}
    >
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-full bg-red-500/20 flex items-center justify-center shrink-0">
          <AlertTriangle className="h-6 w-6 text-red-400" />
        </div>

        <div className="flex-1 min-w-0">
          {/* Header badges */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="px-2 py-0.5 text-xs font-mono bg-[#1F1F1F] text-[#A0A0A0] rounded">
              {obligation.obligation_code || 'N/A'}
            </span>
            <span className="px-2 py-0.5 text-xs font-bold bg-red-500/20 text-red-400 rounded">
              {obligation.gap_type === 'overdue' 
                ? `${getDaysOverdue()} days overdue`
                : obligation.risk_level || 'High Risk'}
            </span>
            <span className="px-2 py-0.5 text-xs bg-[#1F1F1F] text-[#A0A0A0] rounded">
              {(obligation.framework_code || 'General').replace('_', '/')}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-white mb-2">
            {obligation.obligation_title}
          </h3>

          {/* Description */}
          {obligation.description && (
            <p className="text-sm text-[#A0A0A0] mb-3">{obligation.description}</p>
          )}

          {/* Meta grid */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-xs text-[#6B6B6B] mb-1">Due Date</p>
              <p className="text-sm font-medium text-red-400">
                {formatDate(obligation.due_date)}
              </p>
            </div>
            <div>
              <p className="text-xs text-[#6B6B6B] mb-1">Owner</p>
              <p className="text-sm font-medium text-white">
                {obligation.assigned_to?.user_name || 'Unassigned'}
              </p>
            </div>
          </div>

          {/* Impact box */}
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
            <p className="text-xs font-semibold text-red-400 mb-1">Impact</p>
            <p className="text-sm text-red-300">
              {obligation.impact || 'Compliance deadline missed. May result in audit findings or penalties.'}
            </p>
          </div>

          {/* Recommendation */}
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <p className="text-xs font-semibold text-emerald-400 mb-1">Recommendation</p>
            <p className="text-sm text-emerald-300">
              {obligation.recommendation || 'Prioritize completion immediately to minimize impact.'}
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[#1F1F1F]">
        <button 
          className="flex-1 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 text-xs font-medium rounded-lg transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
        >
          View Details
        </button>
        <button 
          className="flex-1 py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 text-amber-400 text-xs font-medium rounded-lg transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            // Escalate action
          }}
        >
          <TrendingUp size={12} className="inline mr-1" />
          Escalate
        </button>
      </div>
    </div>
  );
}
